import { getGraphQLClient } from '@simplywallst/services';
import { TadaDocumentNode } from 'gql.tada';
import type { RequestDocument } from 'graphql-request';
import { getAuthCookie } from '@utilities/cookies';

import { setContext } from '@sentry/react';
import { SWSGQLError, isErrors } from './error';
import { getPreferredLanguage } from './language';

interface SWSGQLHeaders {
  'x-sws-pro-api': string;
}

const getHeaders = (headers: Partial<SWSGQLHeaders> = {}) => {
  let preferredLanguage: string | undefined;
  try {
    preferredLanguage = window.navigator.language;
  } catch {
    preferredLanguage = getPreferredLanguage();
  }

  return {
    'apollographql-client-name': RUNTIME_ENV === 'native' ? 'mobile' : 'web',
    ...(typeof preferredLanguage === 'string'
      ? { 'Accept-Language': preferredLanguage }
      : {}),
    ...headers,
  };
};

export const handleError = (error: unknown) => {
  if (error instanceof Error) {
    const { response } = JSON.parse(JSON.stringify(error));
    if (typeof response === 'object' && 'errors' in response) {
      const { errors } = response;
      if (isErrors(errors)) {
        setContext('gqlQuery', { errors });
        throw new SWSGQLError(errors);
      }
    }
  }
  throw new Error('Error unknown');
};

export const getOperationName = (query: RequestDocument) => {
  if (typeof query === 'string') {
    const matches = query.match(/(?:query|mutation)\s+(\w+)\s*\(/);

    return matches ? matches[1] : null;
  }

  /**
   * Since we are using gql package from graphql-request to wrap our queries,
   * we don't need to handle DocumentNode parsing as it always returns a string with interpolated variables.
   * Should this ever change, we can add support for DocumentNode parsing.
   */
  return null;
};

function makeGQLClient() {
  return {
    request: async <
      Query extends TadaDocumentNode,
      Result = Query extends TadaDocumentNode<infer R> ? R : never,
      Variables = Query extends TadaDocumentNode<Result, infer V> ? V : never,
    >(
      query: TadaDocumentNode<Result, Variables>,
      variables: Variables,
      headers: Partial<SWSGQLHeaders> = {}
    ) => {
      const client = getGraphQLClient({
        headers: getHeaders(headers),
        baseURL:
          typeof SWS_INTERNAL_GQL_URL !== 'undefined'
            ? SWS_INTERNAL_GQL_URL
            : undefined,
      });

      setContext('gql', { operationName: getOperationName(query) });

      const auth = await getAuthCookie();

      if (typeof auth !== 'undefined') {
        client.setHeader('Authorization', `Bearer ${auth.accessToken}`);
      }

      const response = await client
        .request<Result & { errors?: [{ message: string }] }, Variables>(
          query,
          variables
        )
        .catch(handleError);

      return response;
    },
  };
}

export const gqlClient = makeGQLClient();
